import posthog, { type Properties } from "posthog-js";
import { isAnalyticsReady, silently, toTrackingCode } from "./analytics";
import { flushPendingLogin } from "./login";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The signed-in user as analytics reads it. Any field may be missing. */
export interface AnalyticsUser {
  id?: string | null;
  email?: string | null;
  locale?: string | null;
  isSuperAdmin?: boolean | null;
  isInternalStaff?: boolean | null;
  isImpersonating?: boolean | null;
  viewsWithNotificationsCount?: number | null;
}

/** The active company as analytics reads it. Any field may be missing. */
export interface AnalyticsCompany {
  id?: string | null;
  name?: string | null;
  statutoryName?: string | null;
  subscriptionPlan?: string | null;
  subscriptionTerm?: string | null;
  subscriptionStatus?: string | null;
  isOnTrial?: boolean | null;
  hasUsedTrial?: boolean | null;
  trialEndsAt?: string | null;
  trialDurationDays?: number | null;
  subscriptionStartedAt?: string | null;
  additionalSeats?: number | null;
  onboardingCompletedAt?: string | null;
  isDemo?: boolean | null;
  isPartner?: boolean | null;
  featureQuotas?: ReadonlyArray<{
    feature?: string | null;
    limit?: number | null;
  } | null> | null;
}

/**
 * Route guards call the sync functions on every navigation, so each remembers
 * what it last sent and skips PostHog calls that would repeat it.
 */
let identifiedAs: string | undefined;
const sentCompanyProperties = new Map<string, string>();

/**
 * Called the moment an account exists. This is what stitches the anonymous
 * pre-signup session to the new user, so the signup funnel stays one person.
 */
export const identifyNewAccount = (userId?: string | null): void =>
  silently(() => {
    if (!isAnalyticsReady() || !userId) return;
    posthog.identify(userId);
  });

/** The domain part of an email address; the address itself is never sent. */
const emailDomain = (email?: string | null): string | undefined => {
  const at = email?.lastIndexOf("@") ?? -1;
  if (!email || at < 0) return undefined;
  return (
    email
      .slice(at + 1)
      .trim()
      .toLowerCase() || undefined
  );
};

const personProperties = (user: AnalyticsUser): Properties => ({
  email_domain: emailDomain(user.email),
  locale: user.locale ?? undefined,
  is_super_admin: user.isSuperAdmin ?? undefined,
  is_internal_staff: user.isInternalStaff ?? undefined,
  notifying_view_count: user.viewsWithNotificationsCount ?? undefined,
});

/**
 * Identifies the signed-in user. A super admin impersonating a customer is not
 * that customer, so capture is switched off for the whole impersonation and
 * switched back on once they are themselves again.
 */
export const syncAnalyticsIdentity = (user?: AnalyticsUser | null): void =>
  silently(() => {
    if (!isAnalyticsReady() || !user) return;

    if (user.isImpersonating) {
      if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
      identifiedAs = undefined;
      sentCompanyProperties.clear();
      return;
    }

    // Nothing else in the app opts out, so an opt-out here is left over from
    // an impersonation. `captureEventName: false` keeps it from sending `$opt_in`.
    if (posthog.has_opted_out_capturing()) {
      posthog.opt_in_capturing({ captureEventName: false });
    }

    if (!user.id) return;
    const properties = personProperties(user);
    const fingerprint = JSON.stringify([user.id, properties]);
    if (fingerprint === identifiedAs) return;

    posthog.identify(user.id, properties);
    identifiedAs = fingerprint;
  });

const featureLimits = (
  quotas: AnalyticsCompany["featureQuotas"],
): Properties => {
  const limits: Properties = {};
  for (const quota of quotas ?? []) {
    const feature = toTrackingCode(quota?.feature);
    if (feature && typeof quota?.limit === "number") {
      limits[`${feature}_limit`] = quota.limit;
    }
  }
  return limits;
};

const companyProperties = (company: AnalyticsCompany): Properties => {
  const status = toTrackingCode(company.subscriptionStatus);
  return {
    ...featureLimits(company.featureQuotas),
    // GraphQL sends an empty string for a company without a trading name.
    name: company.name || company.statutoryName || undefined,
    subscription_plan: toTrackingCode(company.subscriptionPlan),
    subscription_term: toTrackingCode(company.subscriptionTerm),
    subscription_status: status,
    is_on_trial: company.isOnTrial ?? undefined,
    has_used_trial: company.hasUsedTrial ?? undefined,
    trial_ends_at: company.trialEndsAt ?? undefined,
    trial_duration_days: company.trialDurationDays ?? undefined,
    subscription_started_at: company.subscriptionStartedAt ?? undefined,
    additional_seat_count: company.additionalSeats ?? undefined,
    onboarding_completed_at: company.onboardingCompletedAt ?? undefined,
    is_demo: company.isDemo ?? undefined,
    is_partner: company.isPartner ?? undefined,
    has_converted_from_trial:
      company.hasUsedTrial === true && status === "active",
  };
};

/**
 * Day 1 is the first day of the trial, counted from its end minus its length.
 * Clamped to the trial, so clock skew never reports day 0 or day 15 of 14.
 */
const trialDay = (company: AnalyticsCompany): number | undefined => {
  const duration = company.trialDurationDays;
  const endsAt = Date.parse(company.trialEndsAt ?? "");
  if (!company.isOnTrial || !duration || duration < 1) return undefined;
  if (!Number.isFinite(endsAt)) return undefined;

  const startedAt = endsAt - duration * DAY_MS;
  const day = Math.floor((Date.now() - startedAt) / DAY_MS) + 1;
  return Math.min(Math.max(day, 1), duration);
};

const syncSuperProperty = (name: string, value?: string | number): void => {
  if (value === undefined) posthog.unregister(name);
  else posthog.register({ [name]: value });
};

/**
 * Makes the company the user is working in the `company` group of every event
 * that follows. `group()` with properties sends a `$groupidentify` event on each
 * call, so properties are only passed when they changed for that company; the
 * bare call still switches the active group.
 */
export const syncAnalyticsCompany = (company?: AnalyticsCompany | null): void =>
  silently(() => {
    if (!isAnalyticsReady() || posthog.has_opted_out_capturing()) return;
    const companyId = company?.id;
    if (!company || !companyId) return;

    // The account id rides on every event as well as on the group: per-account
    // insights then work as an event breakdown, with or without PostHog's
    // group analytics add-on. Registered first, so the `$groupidentify` sent
    // below already carries the new company.
    syncSuperProperty("company_id", companyId);
    syncSuperProperty(
      "subscription_plan",
      toTrackingCode(company.subscriptionPlan),
    );
    syncSuperProperty("trial_day", trialDay(company));

    const properties = companyProperties(company);
    const serialised = JSON.stringify(properties);
    if (sentCompanyProperties.get(companyId) === serialised) {
      posthog.group("company", companyId);
    } else {
      posthog.group("company", companyId, properties);
      sentCompanyProperties.set(companyId, serialised);
    }

    // The company is now on every event, so a login waiting for it can go.
    flushPendingLogin();
  });

/** On logout: the next person on this browser starts anonymous. */
export const resetAnalytics = (): void =>
  silently(() => {
    identifiedAs = undefined;
    sentCompanyProperties.clear();
    if (!isAnalyticsReady()) return;
    posthog.reset();
  });
